/* eslint-disable */
// BBOT — box-plot report (React port of the polished single-page design).
//   • PERCENTILE bars (baseball marker on a blue→coral track) — the ten measured tools, ADMIN-ONLY.
//   • MEASURED MECHANICS — median/IQR box-plots per captured pose metric (result-only, not ranked).
//   • SCOUTING & MECHANICAL OBSERVATIONS — the eval's sections with 20–80 grade pills.
// 100% data-driven from the report shape (measuredValues / percentiles / poseMetrics.deliveries / sections).
// Firewall-clean language is enforced upstream (assertion firewall); this component only renders.
import React from "react";
import { MechanicsChart, ladderStatesValues } from "./MechanicsChart";

// Same backend the app uses; declared here because this module renders independently of App.js.
const BBOT_BACKEND = "https://gracious-wonder-production-df2d.up.railway.app";
const BLUE = "#2970b8", CORAL = "#e8526a", NAVY = "#0f172a", INK2 = "#8a98b0";
const GRAD = "linear-gradient(90deg,#2970b8 0%,#5575c6 38%,#9a73ac 60%,#ec6f86 82%,#e8526a 100%)";

// the locked ten — label + unit + direction (higher/lower better)
const PCT = {
  exitVeloPeak: ["Power (peak EV)", "mph"], exitVeloAvg: ["Power (avg EV)", "mph"],
  pitchVeloPeak: ["Fastball (peak)", "mph"], pitchVeloAvg: ["Fastball (avg)", "mph"],
  of_throw_velo_top: ["OF Arm", "mph"], if_throw_velo_top: ["IF Arm", "mph"], catcher_throw_velo_top: ["Catcher Arm", "mph"],
  dash60: ["60-Yard", "s"], h2f: ["Home-to-1st", "s"], popTime: ["Pop Time", "s"],
};
const PCT_ORDER = ["exitVeloPeak", "exitVeloAvg", "pitchVeloPeak", "pitchVeloAvg",
  "of_throw_velo_top", "if_throw_velo_top", "catcher_throw_velo_top", "dash60", "h2f", "popTime"];
// pose metrics per player kind: [label, key, unit, decimals]
const ML_HIT = [["Hip-shoulder sep", "max_hip_shoulder_separation_deg", "°", 0], ["Shoulder-turn rate", "peak_shoulder_turn_deg_s", "°/s", 0],
  ["Stride", "stride_pct_height", "%", 0], ["Weight shift", "weight_shift_pct", "%", 0],
  ["Shoulder-line tilt", "torso_tilt_deg_at_contact", "°", 0], ["Head movement", "head_movement_pct", "%", 0],
  ["Swing tempo", "foot_plant_to_contact_s", "s", 2]];
const ML_PITCH = [["Knee lift", "knee_lift_height_pct", "%", 0], ["Arm slot", "arm_slot_deg", "°", 0],
  ["Hip-shoulder sep", "max_hip_shoulder_separation_deg", "°", 0],
  ["Release height", "release_height_pct", "%", 0], ["Stride", "stride_pct_height", "%", 0]];
// Plain-English "what it is" per mark, keyed by metric — for the glossary strip so a reader isn't lost.
const GLOSS = {
  arm_slot_deg: "the angle of the throwing arm at release — higher is more over-the-top, lower is more sidearm",
  max_hip_shoulder_separation_deg: "how far the hips rotate ahead of the shoulders — the coil that makes power",
  release_height_pct: "how high the ball is when it leaves the hand",
  knee_lift_height_pct: "how high the front knee comes up, as a share of his height",
  stride_pct_height: "how far he strides forward, as a share of his height",
  weight_shift_pct: "how much he moves into contact",
  torso_tilt_deg_at_contact: "sideways lean of the torso at contact",
  head_movement_pct: "how still the head stays through the swing",
  peak_shoulder_turn_deg_s: "how fast the shoulders rotate",
  foot_plant_to_contact_s: "time from foot plant (load) to contact",
};

function SeamDot({ px }) {
  return (
    <svg width={px} height={px} viewBox="0 0 24 24" style={{ display: "block", filter: "drop-shadow(0 1px 1.3px rgba(0,0,0,.3))" }}>
      <circle cx="12" cy="12" r="11" fill={BLUE} stroke="#fff" strokeWidth="1.3" />
      <path d="M6.5 3.8 C9 7,9 17,6.5 20.2" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
      <path d="M17.5 3.8 C15 7,15 17,17.5 20.2" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}
// Section headers and blocks carry print classes so the PDF can break at boundaries only. Marking
// them here (rather than in the print CSS by tag) keeps the rule tied to the structure it protects.
function Hdr({ title, sub }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, margin: "2px 0 6px" }} className="bbot-sec-hdr" data-section-heading="1">
      <span style={{ width: 3, height: 13, background: CORAL, borderRadius: 2, flexShrink: 0 }} />
      <span style={{ font: "800 10.5px Inter,Arial", color: BLUE, letterSpacing: 1.2 }}>{title}</span>
      {sub ? <span style={{ font: "600 8.5px Inter,Arial", color: "#9aa7bd" }}>&nbsp;·&nbsp;{sub}</span> : null}
    </div>
  );
}
function PctBar({ label, value, unit, display, pct }) {
  const left = Math.max(4, Math.min(96, ((pct - 20) / 79) * 100));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "5px 0 4px" }}>
      <div style={{ width: 112, font: "700 12px Inter,Arial", color: BLUE }}>{label}</div>
      <div style={{ position: "relative", flex: 1, height: 13, margin: "6px 4px" }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: 7, background: GRAD }} />
        <div style={{ position: "absolute", left: left + "%", top: -14, transform: "translateX(-50%)", font: "800 11.5px Inter,Arial", color: NAVY, whiteSpace: "nowrap" }}>{display}</div>
        <div style={{ position: "absolute", left: left + "%", top: "50%", transform: "translate(-50%,-50%)" }}><SeamDot px={15} /></div>
      </div>
      <div style={{ width: 78, textAlign: "right", font: "700 12px Inter,Arial", color: BLUE }}>{value != null ? value + " " + unit : ""}</div>
    </div>
  );
}
function BoxRow({ label, values, unit, dec, repWord }) {
  const vs = (values || []).filter((v) => v != null && !isNaN(v)).sort((a, b) => a - b), n = vs.length;
  if (!n) return null;   // no valid values -> render nothing (never crash on an empty/undefined series)
  const q1 = vs[Math.floor(n / 4)], med = vs[Math.floor(n / 2)], q3 = vs[Math.min(n - 1, Math.floor((3 * n) / 4))];
  const iqr = q3 - q1, lf = q1 - 1.5 * iqr, uf = q3 + 1.5 * iqr;
  const inl = vs.filter((v) => v >= lf && v <= uf), wlo = Math.min.apply(null, inl), whi = Math.max.apply(null, inl);
  const hiOut = vs.filter((v) => v > uf), loOut = vs.filter((v) => v < lf), nout = hiOut.length + loOut.length;
  const TW = 210, L = loOut.length ? 16 : 3, R = hiOut.length ? TW - 16 : TW - 3;
  const axlo = wlo, axhi = whi > wlo ? whi : wlo + 1;
  const X = (v) => L + ((Math.max(axlo, Math.min(axhi, v)) - axlo) / (axhi - axlo)) * (R - L);
  const usp = unit === "°" || unit === "%" ? "" : " ";
  const f = (v) => v.toFixed(dec);
  const ex = nout ? (hiOut.length ? Math.max.apply(null, hiOut) : Math.min.apply(null, loOut)) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "1.5px 0" }}>
      <div style={{ width: 120, font: "600 10px Inter,Arial", color: "#475569", textAlign: "right" }}>{label}</div>
      <div style={{ width: TW, height: 14, position: "relative" }}>
        <div style={{ position: "absolute", left: X(wlo), top: 6, height: 2, width: X(whi) - X(wlo), background: "#94a3b8" }} />
        <div style={{ position: "absolute", left: X(q1), top: 1, width: Math.max(2, X(q3) - X(q1)), height: 12, background: "rgba(242,106,126,.28)", border: "1.3px solid " + CORAL, borderRadius: 2 }} />
        <div style={{ position: "absolute", left: X(med) - 1.2, top: 0, width: 2.4, height: 14, background: BLUE }} />
        {hiOut.length ? <div style={{ position: "absolute", left: X(whi), top: 6, height: 2, width: TW - 6 - X(whi), borderTop: "2px dashed #cbd5e1" }} /> : null}
        {hiOut.length ? <div style={{ position: "absolute", left: TW - 9, top: 3.5, width: 7, height: 7, borderRadius: "50%", background: CORAL, border: "1.4px solid #a01830", boxShadow: "0 0 0 1.4px #fff" }} /> : null}
        {loOut.length ? <div style={{ position: "absolute", left: 3, top: 6, height: 2, width: X(wlo) - 3, borderTop: "2px dashed #cbd5e1" }} /> : null}
        {loOut.length ? <div style={{ position: "absolute", left: 2, top: 3.5, width: 7, height: 7, borderRadius: "50%", background: CORAL, border: "1.4px solid #a01830", boxShadow: "0 0 0 1.4px #fff" }} /> : null}
      </div>
      <div style={{ font: "600 9.5px Inter,Arial", color: "#334155", whiteSpace: "nowrap" }}>
        typical <b style={{ color: BLUE }}>{f(med) + usp + unit}</b>{" "}
        <span style={{ color: "#64748b" }}>· middle half of {repWord} {f(q1) + usp + "–" + f(q3) + usp + unit}</span>
        {nout ? <span style={{ color: CORAL, fontWeight: 700 }}> · {nout} outlier{nout > 1 ? "s" : ""} → {f(ex) + usp + unit}</span> : null}
      </div>
    </div>
  );
}

// Per-metric display scale [min,max] for placing the baseball on the track (result-only value marker,
// not a percentile — the track is the same blue→coral style as PERCENTILE for visual consistency).
const SCALE = {
  knee_lift_height_pct: [40, 100], arm_slot_deg: [20, 90], max_hip_shoulder_separation_deg: [10, 70],
  release_height_pct: [70, 110], stride_pct_height: [20, 110], peak_shoulder_turn_deg_s: [0, 1200],
  weight_shift_pct: [0, 40], torso_tilt_deg_at_contact: [0, 45], head_movement_pct: [0, 25],
  foot_plant_to_contact_s: [0, 1.2],
};
// Compact baseball-marker bar: the value on a blue→coral track (percentile-graph style). Sized small for
// a 3-column grid; used for measured mechanics (both kinds) AND pitch velocities.
// THE UNIFORM METRIC VISUAL. One treatment for every measured value -- mechanics medians, velocity,
// times, per-delivery consistency. The visual IS the representation: no caption, no prose restating
// the number, because a value with two homes is a value that can disagree with itself.
//
// The label rule rides INSIDE and costs nothing: a bar positioned "vs 18U benchmark" and one at
// "68th percentile" are the same bar with one word different. Real-pool cells say percentile;
// authored or thin cells say benchmark. No design fork, no second component.
//
// `invert` handles faster-is-better marks (times), so a low value sits on the GOOD end like every
// other metric -- one direction convention for the reader across the whole report.
function MetricBar({ label, value, unit, dec, lo, hi, refLabel, invert }) {
  if (value == null || isNaN(value)) return null;
  var span = (hi - lo) || 1;
  var frac = (value - lo) / span;
  if (invert) frac = 1 - frac;
  var left = Math.max(7, Math.min(93, frac * 100));
  var usp = (unit === "°" || unit === "%") ? "" : " ";
  return (
    <div style={{ padding: "2px 0 6px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", font: "700 8.5px Inter,Arial", color: BLUE }}>
        <span>{label}</span><b>{(dec != null ? value.toFixed(dec) : value) + usp + unit}</b>
      </div>
      <div style={{ position: "relative", height: 10 }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: 5, background: GRAD }} />
        <div style={{ position: "absolute", left: left + "%", top: "50%", transform: "translate(-50%,-50%)" }}><SeamDot px={12} /></div>
      </div>
      {refLabel ? <div style={{ font: "600 8px Inter,Arial", color: "#8a93a3", marginTop: 2 }}>{refLabel}</div> : null}
    </div>
  );
}

function MechBar({ label, value, unit, dec, lo, hi }) {
  if (value == null || isNaN(value)) return null;   // missing metric -> omit the bar, never crash
  const left = Math.max(7, Math.min(93, ((value - lo) / (hi - lo)) * 100));
  const usp = unit === "°" || unit === "%" ? "" : " ";
  return (
    <div style={{ padding: "2px 0 5px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", font: "700 8.5px Inter,Arial", color: BLUE, marginBottom: 3 }}>
        <span>{label}</span><b>{value.toFixed(dec) + usp + unit}</b>
      </div>
      <div style={{ position: "relative", height: 10 }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: 5, background: GRAD }} />
        <div style={{ position: "absolute", left: left + "%", top: "50%", transform: "translate(-50%,-50%)" }}><SeamDot px={12} /></div>
      </div>
    </div>
  );
}

// Graphed time bar — faster is BETTER, so a lower time sits on the GOOD (right) end of the track.
// slow→fast benchmark span mirrors the grade functions; grade badge rides on the value.
function TimeBar({ label, value, unit, dec, slow, fast, grade, basis }) {
  if (value == null || isNaN(value)) return null;   // missing timed value -> omit the bar, never crash
  const left = Math.max(7, Math.min(93, ((slow - value) / (slow - fast)) * 100));
  return (
    <div style={{ padding: "2px 0 5px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", font: "700 8.5px Inter,Arial", color: BLUE, marginBottom: 3 }}>
        <span>{label}</span>
        <span style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
          <b>{value.toFixed(dec) + " " + unit}</b>
          {grade != null ? <span style={{ background: CORAL, color: "#fff", borderRadius: 10, padding: "0px 7px", font: "800 9px Inter,Arial" }}>{grade}</span> : null}
        </span>
      </div>
      <div style={{ position: "relative", height: 10 }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: 5, background: GRAD }} />
        <div style={{ position: "absolute", left: left + "%", top: "50%", transform: "translate(-50%,-50%)" }}><SeamDot px={12} /></div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", font: "600 7px Inter,Arial", color: INK2, marginTop: 1 }}>
        <span>{slow.toFixed(1) + unit}</span><span>faster →</span><span>{fast.toFixed(1) + unit}</span>
      </div>
      {basis ? <div style={{ font: "600 7px Inter,Arial", color: "#16a34a", marginTop: 2, letterSpacing: .2 }}>✓ {basis}</div> : null}
    </div>
  );
}
function num(v) { return typeof v === "number" ? v : (v != null && v !== "" && !isNaN(+v) ? +v : null); }

// TIMED-tool grades from the RUBRIC DIALS (age-peer percentile -> locked 20-80 curve) — the SAME model the
// backend uses for velo tools, so run/pop grades are age-correct (a 14U 4.25 h2f is elite, not "average").
// Generated from bbot_rubric_config.json (regenerate this block if the Excel/config changes). Faster = better
// handled by the dial direction. Falls back to the old flat HS bands only when the age bracket is unknown.
var _TIMED_CFG = {"brackets":["13U","14U","15U","16U","17U","18U","Top College","College","Pro-MLB"],"aliases":{"19+":"College","12U-13U":"13U","12U":"13U","13U-14U":"13U","14U-15U":"14U","16U-17U":"17U","18U+":"18U","19U":"College","College+":"College"},"pcts":[10,20,30,40,50,60,70,80,90,99],"k":12.8957,"clamp":[20,80],"snap":5,"lhh_h2f_adjust":0.10,"dials":{"h2f":{"anchor":3.9,"factors":[1.122,1.098,1.073,1.061,1.049,1.037,1.024,1.012,1],"spread":[1.1787,1.1529,1.1281,1.1023,1.0764,1.0603,1.0452,1.0291,1.014,1]},"dash60":{"anchor":6.1,"factors":[1.197,1.167,1.136,1.106,1.076,1.061,1.045,1.03,1],"spread":[1.1787,1.1529,1.1281,1.1023,1.0764,1.0603,1.0452,1.0291,1.014,1]},"popTime":{"anchor":1.75,"factors":[1.179,1.154,1.128,1.103,1.077,1.051,1.036,1.026,1],"spread":[1.1787,1.1529,1.1281,1.1023,1.0764,1.0603,1.0452,1.0291,1.014,1]}}};
// THE AGE RULE — mirrored from bbot_rubric_config.json "age_rule" (regenerate with the block above).
// Age bracket = the player's age on May 1 of the SESSION's calendar year: a fixed anchor, so a player's
// peer group cannot change mid-season on his birthday. seasonAge() in App.js reads THIS — the anchor is
// not hard-coded anywhere, and every graded report records the rule id in gradeBasis.ageRule.
export var AGE_RULE = { id: "may_1@v1", anchorMonth: 5, anchorDay: 1, anchorYear: "session_year" };

// Inverse normal CDF (probit), Acklam's rational approximation — matches Python NormalDist().inv_cdf.
function _probit(p) {
  if (p <= 0) return -Infinity; if (p >= 1) return Infinity;
  var a=[-3.969683028665376e+01,2.209460984245205e+02,-2.759285104469687e+02,1.383577518672690e+02,-3.066479806614716e+01,2.506628277459239e+00];
  var b=[-5.447609879822406e+01,1.615858368580409e+02,-1.556989798598866e+02,6.680131188771972e+01,-1.328068155288572e+01];
  var c=[-7.784894002430293e-03,-3.223964580411365e-01,-2.400758277161838e+00,-2.549732539343734e+00,4.374664141464968e+00,2.938163982698783e+00];
  var d=[7.784695709041462e-03,3.224671290700398e-01,2.445134137142996e+00,3.754408661907416e+00];
  var pl=0.02425, ph=1-pl, q, r;
  if (p<pl){ q=Math.sqrt(-2*Math.log(p)); return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5])/((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1); }
  if (p<=ph){ q=p-0.5; r=q*q; return (((((a[0]*r+a[1])*r+a[2])*r+a[3])*r+a[4])*r+a[5])*q/(((((b[0]*r+b[1])*r+b[2])*r+b[3])*r+b[4])*r+1); }
  q=Math.sqrt(-2*Math.log(1-p)); return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5])/((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
}
function _normBracket(b){ if(!b) return null; if(_TIMED_CFG.brackets.indexOf(b)>=0) return b; return _TIMED_CFG.aliases[b]||b; }
function _timedPct(field, bracket, val){
  var d=_TIMED_CFG.dials[field]; var br=_normBracket(bracket); var bi=_TIMED_CFG.brackets.indexOf(br);
  if(!d||bi<0) return null;
  var f=d.factors[bi];
  var pairs=d.spread.map(function(s,i){ return [d.anchor*f*s, _TIMED_CFG.pcts[i]]; }).sort(function(a,b){ return a[0]-b[0]; });
  var xs=pairs.map(function(p){return p[0];}), ps=pairs.map(function(p){return p[1];});
  if(val<=xs[0]) return ps[0]; if(val>=xs[xs.length-1]) return ps[ps.length-1];
  var i=1; while(i<xs.length && xs[i]<val) i++;
  var x0=xs[i-1],x1=xs[i],p0=ps[i-1],p1=ps[i];
  return p0+(p1-p0)*(val-x0)/(x1-x0);
}
function _gradeFromPct(pct){
  var z=_probit(Math.min(Math.max(pct,0.5),99.5)/100);
  var g=50+_TIMED_CFG.k*z; g=Math.round(g/_TIMED_CFG.snap)*_TIMED_CFG.snap;
  return Math.max(_TIMED_CFG.clamp[0], Math.min(_TIMED_CFG.clamp[1], g));
}
// legacy flat bands — fallback ONLY when bracket unknown (never age-grade blind is preferable to no grade)
function _h2fBand(t,bats){ var a=String(bats||"").toUpperCase().charAt(0)==="L"?t+0.12:t; return a<=4.0?70:a<=4.15?60:a<=4.25?55:a<=4.4?50:a<=4.55?45:a<=4.7?40:30; }
function _dash60Band(t){ return t<=6.5?70:t<=6.8?60:t<=7.0?55:t<=7.2?50:t<=7.5?45:t<=7.8?40:30; }
function _popBand(t){ return t<=1.9?70:t<=2.0?60:t<=2.05?55:t<=2.15?50:t<=2.25?45:t<=2.35?40:30; }
// timed-tool percentile + grade from the dials. Returns {grade, percentile} or null (unknown bracket).
export function timedDialGrade(field, v, bats, bracket){
  if(v==null||!_TIMED_CFG.dials[field]) return null;
  var val=(field==="h2f" && String(bats||"").toUpperCase().charAt(0)==="L") ? v+_TIMED_CFG.lhh_h2f_adjust : v;
  var pct=_timedPct(field, bracket, val);
  if(pct==null) return null;
  return { grade:_gradeFromPct(pct), percentile:Math.round(pct*10)/10 };
}
// grade a timed field — DIAL-based (age-peer) when bracket is known, else legacy flat band. Exported so the
// dashboard tag-screen timer grades identically to the report timer (one source of truth).
export function gradeForField(field, v, bats, bracket) {
  if (v == null) return null;
  var dg = timedDialGrade(field, v, bats, bracket);
  if (dg) return dg.grade;
  return field === "h2f" ? _h2fBand(v, bats) : field === "dash60" ? _dash60Band(v) : field === "popTime" ? _popBand(v) : null;
}
// gradeBasis for the disclosure track on a timed tool (value + age-peer percentile + bracket). null if no bracket.
export function timedGradeBasis(field, v, bats, bracket) {
  var dg = timedDialGrade(field, v, bats, bracket);
  if (!dg) return null;
  return { value: v, unit: "s", percentile: dg.percentile, bracket: _normBracket(bracket), source: "rubric_dial" };
}

// GRADE DISCLOSURE track — the measured value's age-peer percentile on the locked blue->coral SeamDot
// track, shown under a graded tool so the report DISCLOSES what drives the grade. Outputs only (value,
// percentile, bracket) — never the dials/method. Renders only when gradeBasis is present (fallback-safe).
// BAND LANGUAGE DERIVES FROM THE GRADE, never from a second computation. A separately-computed band
// produced "4s at the 18U benchmark" beside an 80 Run grade -- two authorities on one value. The grade
// is the rubric's output, so the phrase is a restatement of it and cannot contradict it.
function bandFromGrade(g) {
  if (g == null || isNaN(+g)) return "";
  var n = +g;
  if (n >= 75) return "top of the age band";
  if (n >= 65) return "above the age band";
  if (n >= 45) return "at the age band";
  return "below the age band";
}

// Drops any sentence containing a value the measured blocks already carry. Tolerant of rounding
// (0.47 vs 0.469) because the same fact printed two ways is the defect being removed.
function dropRestated(text, owned) {
  if (!text || !owned || !owned.length) return text;
  return String(text).split(/(?<=\.)\s+/).filter(function (sent) {
    const nums = sent.match(/\d+(?:\.\d+)?/g) || [];
    return !nums.some(function (n) {
      const f = parseFloat(n);
      return owned.some(function (v) {
        return Math.abs(v - f) < 0.05 || Math.round(v) === Math.round(f);
      });
    });
  }).join(" ").trim();
}

// SUMMARY DERIVES FROM GRADES so it cannot contradict them. The stored summary is prose written on a
// different path and drifted: "plus hit" printed above a Hit grade of 40, "above-average arm" above a
// Throw of 45, and a 17U bracket beside an 18U header.
var GRADE_WORDS = [[75, "top of scale"], [65, "plus"], [55, "above-average"], [45, "average"], [35, "below-average"], [0, "well below average"]];
function gradeWord(g) {
  for (var i = 0; i < GRADE_WORDS.length; i++) if (+g >= GRADE_WORDS[i][0]) return GRADE_WORDS[i][1];
  return "well below average";
}
// The client-side composer was REMOVED here, not disabled. A dead composition path is how this
// drift regrows: the next person finds a function that builds a summary and wires it back in.
function summaryFromGrades(name, position, bracket, secs) {
  const graded = (secs || []).filter(function (s) { return s.grade != null && !isNaN(+s.grade); });
  const head = [name, [position, bracket].filter(Boolean).join(", ")].filter(Boolean).join(" — ");
  if (!graded.length) return head + ". No tool graded from this session's capture.";
  const rank = graded.slice().sort(function (a, b) { return +b.grade - +a.grade; });
  const top = rank.filter(function (s) { return +s.grade >= 55; });
  const dev = rank.filter(function (s) { return +s.grade < 45; });
  const bits = top.map(function (s) {
    return gradeWord(s.grade) + " " + String(s.label || s.key).toLowerCase();
  });
  if (dev.length) {
    bits.push(dev.map(function (s) { return String(s.label || s.key).toLowerCase(); }).join(", ") +
      " the development " + (dev.length === 1 ? "area" : "areas"));
  }
  const body = bits.join("; ");
  return head + ". " + body.charAt(0).toUpperCase() + body.slice(1) + ".";
}

// GradeTrack REMOVED 2026-10-09 -- see the note at its former call site below, and
// `bbotMeasured.js` for the ruling. The dials it read are still here because the
// timed GRADE helpers use them; nothing draws a percentile from them any more.

export function BbotBoxReport({ r, sc, isAdmin, logo, chart, video, pitchZone, onGrade, onMeasureTime }) {
  // THIS SESSION'S APPROVED CLIPS, resolved by the generator and stored on the report. The list is
  // already session-scoped and approved-only upstream; this only turns keys into playable URLs.
  const sessionClips = (r && (r.sessionClips || (r.fullReport && r.fullReport.sessionClips))) || [];
  const [clipUrls, setClipUrls] = React.useState({});
  React.useEffect(function () {
    let alive = true;
    (sessionClips || []).slice(0, 4).forEach(function (c) {
      if (!c || !c.key) return;
      fetch(BBOT_BACKEND + "/r2-read-url?objectKey=" + encodeURIComponent(c.key))
        .then(function (res) { return res.json(); })
        .then(function (d) {
          if (alive && d && d.url) setClipUrls(function (m) { return Object.assign({}, m, { [c.key]: d.url }); });
        })
        .catch(function () { /* a clip that will not resolve simply does not render */ });
    });
    return function () { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify((sessionClips || []).map(function (c) { return c && c.key; }))]);
  function clipUrl(k) { return clipUrls[k] || ""; }

  r = r || {}; sc = sc || {};
  const [edits, setEdits] = React.useState({});   // admin grade overrides, keyed by section index
  // Run/pop times are ADDED from the dashboard queue (not a per-report stopwatch); here we only DISPLAY them.
  const mv = r.measuredValues || {};
  const mvb = r.measuredValuesBasis || (r.fullReport && r.fullReport.measuredValuesBasis) || (r.full_report && r.full_report.measuredValuesBasis) || {};
  const pc = (r.percentiles && r.percentiles.metrics) ? r.percentiles.metrics : null;
  const bracket = (r.percentiles && r.percentiles.bracket) || sc.ageBracket || "";
  const pm = r.poseMetrics || {};
  const deliveries = pm.deliveries || pm.swings || [];
  // Player kind drives which tools this report shows. Prefer the measured source (pose kind), then the
  // eval flag, then the PLAYER PROFILE position — so a report whose blob is thin still renders as the
  // player it belongs to instead of silently falling back to the hitter layout.
  const _posIsPitcher = /^(P|RHP|LHP|SP|RP|CP)$/i.test(String(sc.position || r.position || "").trim().split(/[\s,/]/)[0]);
  const kind = pm.kind || ((r.isPitcher === true || _posIsPitcher) ? "pitcher" : "hitter");
  // Pose over-detects reps vs the true swing/delivery count (radar owns the count), so these box-plots
  // describe the pose-TRACKED reps, never asserted as a swing count. Label them honestly as "tracked reps".
  const repWord = "tracked reps";
  const name = (sc.firstName || "") + " " + (sc.lastName || "");
  // CLASS YEAR IS A RECRUITING FIELD, and it stops meaning anything once a player is past it.
  // Josue's report carried "Class 2020" beside a 24-year-old -- arithmetically correct against his
  // date of birth (verified: zero class/dob mismatches across every profile), and useless to a reader.
  // A field that is accurate but uninformative still costs credibility, because it reads as stale
  // data. Shown while it is still ahead of or near the present; dropped once it is behind.
  const _classYear = (function () {
    var gy = parseInt(sc.gradYear, 10);
    if (!gy) return null;
    var nowY = new Date().getFullYear();
    return gy >= nowY - 1 ? "Class " + gy : null;
  })();
  const meta = [sc.position, _classYear, bracket,
    (sc.bats || sc.throws) ? ((sc.bats || "-") + "/" + (sc.throws || "-")) : null,
    r.sessionType, r.date].filter(Boolean).join(" · ");

  // percentile bars — measured tools only, admin-gated (blocker 1: players don't see percentiles yet)
  const bars = (isAdmin && pc) ? PCT_ORDER.map(function (k) {
    const m = pc[k]; if (!m || m.percentile == null) return null;
    const meta2 = PCT[k]; const raw = num(mv[k]) != null ? mv[k] : (k.indexOf("throw") >= 0 ? mv.throwVelo : null);
    return <PctBar key={k} label={meta2[0]} value={raw} unit={meta2[1]} display={m.display || (m.percentile + "th")} pct={Number(m.percentile)} />;
  }).filter(Boolean) : [];

  // measured-mechanics baseball-marker bars — median value per captured pose metric (3-column grid)
  const mlDef = kind === "pitcher" ? ML_PITCH : ML_HIT;
  // ONE SOURCE FOR BOTH SURFACES. The summary reads mechanicsTiers -- the pipeline's published,
  // credibility-gated, rep-counted form -- while this body read the raw per-rep list behind its own
  // 5-rep floor. A pitcher whose release height was measured and published at High confidence across
  // the deliveries that tracked cleanly therefore had it ASSERTED in his summary and shown NOWHERE on
  // his page, so the report looked empty while claiming a number. If the summary can assert it, the
  // body shows it; if it cannot, neither speaks. The raw path remains for payloads with no tiers.
  // THE CREDIBILITY LAW, ON THE RENDER PATH (ratified 2026-08-13). BBOT renders ONLY what is
  // credibly measured, and for a pose-measured mechanic "credibly measured" has exactly one
  // meaning: a `mechanicsCertified` block that survives the six-receipt chain and the 5-rep floor.
  //
  // WHAT THIS REPLACES, AND WHY IT HAD TO GO. This mapped two other ways to a number, and neither
  // was a receipt:
  //   * `mechanicsTiers[k].tier === "measured"` -- a label the pipeline wrote about itself;
  //   * a median over the raw per-rep list behind a bare `vals.length < 5` floor -- five values
  //     and no chain, which is precisely the "bounds are the floor, frames are the bar" mistake.
  // A sweep of every value in the database found 810 pose-measured numbers stored and ZERO
  // carrying the chain, so this fallback was the path by which a 2500 deg/s shoulder turn or a
  // 109% knee lift could reach a family in the same font as a device reading. The server strips
  // these from the player payload, but a render gate that only works because the payload happened
  // to be stripped is not a gate. This is the gate.
  const certified = (function () {
    // `_fr` is declared 200 lines below this and `const` does not hoist, so reaching for it here
    // threw a TDZ ReferenceError that the production build compiled happily and the render-fidelity
    // check caught on the first run. The lookup is done locally instead of moving a declaration
    // half the component depends on.
    const _frLocal = r.fullReport || r.full_report || {};
    const blk = r.mechanicsCertified || _frLocal.mechanicsCertified || null;
    const mx = (blk && blk.metrics) || null;
    if (!mx || !Array.isArray(blk.reps)) return {};
    const NEED = ["pool_clean", "anatomy_verified", "scale_hand_measured",
                  "reproduced_from_record", "scale_domain_stable", "plant_event_present"];
    const out = {};
    Object.keys(mx).forEach(function (k) {
      const rc = (mx[k] && mx[k].receipts) || {};
      if (!NEED.every(function (x) { return rc[x] === true; })) return;
      const vals = blk.reps.map(function (rep) { return num(rep[k]); })
        .filter(function (v) { return v != null; });
      if (vals.length < 5) return;                       // the same floor the composer uses
      const vs = vals.slice().sort(function (a, b) { return a - b; });
      out[k] = vs[Math.floor(vs.length / 2)];
    });
    return out;
  })();
  const rows = mlDef.map(function (d) {
    const v = certified[d[1]];
    if (v == null) return null;
    const scl = SCALE[d[1]] || [v * 0.8, (v * 1.2) || 1];
    return <MechBar key={d[1]} label={d[0]} value={v} unit={d[2]} dec={d[3]} lo={scl[0]} hi={scl[1]} />;
  }).filter(Boolean);
  // pitch-velocity bars (pitchers) — cluster distinct radar readings; fastest cluster = fastball, rest breaking
  const veloBars = (function () {
    if (kind !== "pitcher") return [];
    const pv = (r.pitches || []).map(function (p) { return num(p.velocityMph != null ? p.velocityMph : (p.mph != null ? p.mph : p.radar_mph)); }).filter(function (v) { return v != null; });
    if (!pv.length) return [];
    const vs = pv.slice().sort(function (a, b) { return b - a; });
    const cl = []; let cur = [vs[0]];
    for (let i = 1; i < vs.length; i++) { if (cur[cur.length - 1] - vs[i] > 4) { cl.push(cur); cur = [vs[i]]; } else { cur.push(vs[i]); } }
    cl.push(cur);
    const nm = ["Fastball", "Breaking ball", "Breaking ball", "Off-speed"];
    return cl.slice(0, 4).map(function (c, i) {
      return <MechBar key={"v" + i} label={nm[i] || "Pitch"} value={Math.max.apply(null, c)} unit="mph" dec={0} lo={50} hi={100} />;
    });
  })();
  // measured VELOCITY bars (hitters/position players) — the device-read mph captured this session, graphed
  // like the mechanics. Exit velo (peak + avg) and throw velo (peak + how many throws were clocked, plural).
  // LABEL RULE, one place. A cell whose reference pool is real reads as a percentile; an authored or
  // thin cell reads as a benchmark position. Same number, same visual, one word different.
  const refFor = function (key) {
    const m = pc && pc[key];
    if (!m) return bracket ? ("vs " + bracket + " benchmark") : null;
    if (m.playerDisplay) return m.playerDisplay;
    return (m.poolBacked && m.percentile != null)
      ? (m.display || (m.percentile + "th")) + " percentile"
      : (bracket ? "vs " + bracket + " benchmark" : null);
  };

  const hitVeloBars = (function () {
    if (kind === "pitcher") return [];
    const out = [];
    const evPk = num(mv.exitVeloPeak), evAvg = num(mv.exitVeloAvg), tw = num(mv.throwVelo);
    const twReads = Array.isArray(mv.throwVeloReadings) ? mv.throwVeloReadings.filter(function (v) { return num(v) != null; }) : [];
    // Every measured value carries its reference position in the SAME visual. refFor decides the one
    // word that differs: percentile where the pool is real, benchmark where the cell is authored/thin.
    if (evPk != null) out.push(<MetricBar key="evpk" label="Exit velo · peak" value={evPk} unit="mph" dec={0} lo={50} hi={110} refLabel={refFor("exitVeloPeak")} />);
    if (evAvg != null) out.push(<MetricBar key="evavg" label="Exit velo · avg" value={evAvg} unit="mph" dec={0} lo={50} hi={110} refLabel={refFor("exitVeloAvg")} />);
    if (tw != null) out.push(<MetricBar key="tw" refLabel={refFor("throwVelo")} label={"Throw velo · peak" + (twReads.length > 1 ? " (" + twReads.length + " throws)" : "")} value={tw} unit="mph" dec={0} lo={50} hi={100} />);
    return out;
  })();
  // MEASURED TIMES (run/pop) — BBOT video-timed or device-read; faster is better, graded vs age benchmark.
  // A TIME IS HAND-TIMED OR IT IS NOTHING. `h2f` is the clearest case in the whole record: the
  // admin run/pop-time queue writes it with basis "Verified · admin · 90 ft (home-to-1st)", and the
  // EVAL writes the same field from the model's reading of the footage with basis null. The sweep
  // found eight rows carrying the second kind -- including two approved ones -- and this component
  // rendered both identically, with a grade beside them. Same field, same font, one of them
  // measured by a person with a clock and the other read off a video by a language model.
  const timedOK = function (b) { return String(b || "").toLowerCase().indexOf("verified") >= 0; };
  const timedItems = (function () {
    const out = [];
    const h2f = num(mv.h2f), dash = num(mv.dash60), pop = num(mv.popTime);
    // tuple: [label, value, unit, grade, slow(bad end), fast(good end), basis(assertion)]
    if (h2f != null && timedOK(mvb.h2f)) out.push(["Home-to-1st", h2f, "s", gradeForField("h2f", h2f, sc.bats || r.bats, bracket), 4.7, 3.9, mvb.h2f]);
    if (dash != null && timedOK(mvb.dash60)) out.push(["60-Yard", dash, "s", gradeForField("dash60", dash, null, bracket), 7.8, 6.4, mvb.dash60]);
    if (pop != null && kind !== "pitcher" && timedOK(mvb.popTime)) out.push(["Top Pop", pop, "s", gradeForField("popTime", pop, null, bracket), 2.35, 1.85, mvb.popTime]);
    return out;
  })();
  // A TIME ON THE RECORD WITH NO TIMER BEHIND IT IS AN HONEST ABSENCE, NOT A SILENT ONE. Dropping it
  // without a word would leave the reader thinking nothing was captured, which is a different fact
  // from what happened.
  const timedWithheld = [["Home-to-1st", mv.h2f, mvb.h2f], ["60-Yard", mv.dash60, mvb.dash60],
                         ["Top Pop", mv.popTime, mvb.popTime]]
    .filter(function (x) { return num(x[1]) != null && !timedOK(x[2]); })
    .map(function (x) { return x[0]; });
  // glossary lines for ONLY the marks actually shown — the same certified gate, so the glossary
  // cannot explain a metric the page withheld.
  const glossItems = mlDef.map(function (d) {
    return (certified[d[1]] != null && GLOSS[d[1]]) ? [d[0], GLOSS[d[1]]] : null;
  }).filter(Boolean);

  // scouting sections
  const labelFor = kind === "pitcher"
    ? ["FASTBALL", "BREAKING BALLS", "DELIVERY & FINISH", "ARM SLOT / RELEASE", "COMMAND", "HIP-SHOULDER SEP"]
    : ["RUN", "OF ARM", "", "FIELD", "HIT / CONTACT", "POWER (EV)", "APPROACH", "POWER (EV)"];
  // TOOL-KIND GATE — the eval emits the full tool list and honestly marks the ones a session cannot
  // speak to ("not observable in bullpen format"), so a pitcher's report was rendering Run / Fielding /
  // Plate Discipline cards. The report card and PDF paths already segregate tools this way; this box
  // report was the one renderer with no filter. Same lists, same rules as the built ones (App.js
  // pitcherHideKeys / isPitcherTool), plus the pitcher-report rule that hitter tools (Hit, Power,
  // Approach, Bat-to-Ball) don't belong on a pitcher.
  // SAFETY: a cross-kind box is hidden ONLY when nothing was captured for it — a graded or
  // measured tool is never hidden, so no measured data can be lost to this filter.
  const HIDE_FOR_PITCHER = ["run", "receiving", "fielding", "plateDis", "batBall", "exitVelo",
    "popTime", "throw", "hit", "power", "rawPower", "approach"];
  const PITCHER_TOOL_KEYS = ["fastball", "fastballvelo", "fbvelo", "pitchvelo", "velocity", "armaction",
    "mechanics", "movement", "pitchmovement", "spinning", "spin", "changingspeeds", "command", "mix",
    "delivery", "athleticism", "breakingball"];
  function isPitcherTool(s) {
    const k = String(s.key || "").toLowerCase().replace(/[^a-z]/g, "");
    const lbl = String(s.label || "").toLowerCase();
    if (PITCHER_TOOL_KEYS.indexOf(k) >= 0) return true;
    return /arm action|mechanic|pitch movement|spinning|changing speed|fastball|pitch velocity|delivery|command|pitch mix|breaking ball/.test(lbl);
  }
  function keepTool(s) {
    const crossKind = kind === "pitcher" ? (HIDE_FOR_PITCHER.indexOf(s.key) >= 0) : isPitcherTool(s);
    if (!crossKind) return true;
    if (s.grade != null) return true;                                   // graded → captured, never hide
    if (s.gradeBasis && s.gradeBasis.value != null) return true;        // measured basis → never hide
    return false;
  }
  // ONE VALUE ONE HOME — dedupe by tool key. Stored sections can carry the same tool twice (Santana
  // holds two "Hit" entries) and both used to render. Keeping the graded instance makes the duplicate
  // structurally impossible whatever the payload contains: fix the render, not the data.
  const _seenKey = {};
  const _dedup = (r.sections || []).map(function (s, i) { return [s, i]; })
    .filter(function (p) { return keepTool(p[0]); })
    .sort(function (a, b) {
      return (a[0].grade != null ? 0 : 1) - (b[0].grade != null ? 0 : 1);
    })
    .filter(function (p) {
      const k = String(p[0].key || p[0].label || "").trim().toLowerCase();
      if (!k) return true;
      if (_seenKey[k]) return false;
      _seenKey[k] = 1; return true;
    })
    .sort(function (a, b) { return a[1] - b[1]; });        // original reading order restored

  // Every number the measured blocks own. Collected from the SAME payload the blocks render from, so
  // the two can never disagree about what has already been said.
  const ownedValues = (function () {
    const out = [];
    Object.keys(mv || {}).forEach(function (k) { if (num(mv[k]) != null) out.push(num(mv[k])); });
    // FROM WHAT THE BLOCKS ACTUALLY RENDER, which is now the certified set only. Reading the raw
    // per-rep list here would tell the prose gate that a median is already "owned" by a block that
    // withheld it — so a number the page does not show would be suppressed from the sentences too,
    // and the reader would get neither.
    Object.keys(certified).forEach(function (k) { out.push(certified[k]); });
    return out;
  })();

  // ONE BRACKET SOURCE. `bracket` is resolved once from the payload above and used by the header,
  // the grade tracks and this summary alike, so an 18U header cannot sit over a 17U summary.
  // _bandFor / _summaryMech / _summaryTrends were removed with the client composer. They fed it and
  // nothing else; leaving them would be an invitation to wire a second summary back in.
  const _notMeasured = _dedup.map(function (p) { return p[0]; })
    .filter(function (x) { return x.grade == null; })
    .map(function (x) { return String(x.label || x.key); });
  // Players read the marks narrative; admin keeps the grade-derived line alongside the grades.
  // ONE NARRATIVE, ONE TRUTH. Admin and player read the SAME amplified summary -- reviewing something
  // the customer never receives is reviewing the wrong artifact. The grade layer is shown ADDITIONALLY
  // to admins, clearly labelled, never as a different story. (This branch previously gave admins the
  // grade-derived summary, which made every admin view look like the old format.)
  // THE RENDER DISPLAYS; IT NEVER COMPOSES. This used to recompose its own summary, so the gate
  // audited the stored string while the reader saw a different one -- on a live report they
  // contradicted outright. The summary is composed once server-side, gated, traced and stored; here
  // it is only displayed. Any recomposition between store and display splits the truth and blinds
  // the gate.
  const derivedSummary = r.summary || (r.fullReport && r.fullReport.summary) || "";
  const adminGradeLine = isAdmin
    ? summaryFromGrades(name, sc.position, bracket, _dedup.map(function (p) { return p[0]; }))
    : null;

  const secs = _dedup
    .map(function (p) {
    // i stays the ORIGINAL index into r.sections — the admin grade editor writes back by index,
    // so filtering must never renumber it.
    const s = p[0], i = p[1];
    const txt = (s.text || "").trim(); if (!txt) return null;
    const lab = s.label || (labelFor[i] || "OBSERVATION");
    const g = s.grade;
    return (
      <div key={i} style={{ padding: "4px 0", borderTop: "1px solid #eef2f8" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
          <span style={{ font: "800 11px Inter,Arial", color: BLUE, letterSpacing: .6 }}>{String(lab).toUpperCase()}</span>
          {isAdmin
            ? (function () {
                const eff = (i in edits) ? edits[i] : (g == null ? "" : String(g));
                return <input type="number" min="20" max="80" step="5" value={eff} title="Admin override — 20–80 grade (blank = observed)"
                  onChange={function (e) { const s = e.target.value; setEdits(Object.assign({}, edits, { [i]: s })); if (onGrade) onGrade(i, s === "" ? null : Number(s)); }}
                  style={{ width: 54, textAlign: "center", background: eff !== "" ? CORAL : "#fff", color: eff !== "" ? "#fff" : "#94a3b8", border: "1.5px solid " + (eff !== "" ? CORAL : "#dbe2ec"), borderRadius: 10, padding: "2px 4px", font: "800 11px Inter,Arial", outline: "none" }} />;
              })()
            : (g ? <span style={{ background: CORAL, color: "#fff", borderRadius: 10, padding: "2px 10px", font: "800 11px Inter,Arial" }}>{g}</span>
              : <span style={{ font: "700 8px Inter,Arial", color: "#b6c0d0", letterSpacing: .6 }}>OBSERVED</span>)}
        </div>
        {/* The age-peer track was drawn here. Removed 2026-10-09 with its twin in App.js: the
            percentile is interpolated off `_TIMED_CFG.dials` in this same file -- an anchor, a
            bracket multiplier, a fixed spread -- so no measured player is in it. This renderer is
            not imported by the app today, which is exactly why the call goes too: a dead path with
            the drawing still in it is the one that comes back. */}
        {/* GRADED SECTIONS REFERENCE THE BLOCKS, NEVER RESTATE THEM. Tempo rendered 0.47 in MEASURED
            MECHANICS and 0.469 in this prose -- two values for one fact. Peak exit velo appeared in
            three places. Any sentence repeating a number the blocks already own is dropped; what
            remains is the qualitative read and the disclosure, which is this section's actual job. */}
        <div style={{ font: "400 10.5px Inter,Arial", color: "#33415c", lineHeight: 1.55 }}>{dropRestated(txt, ownedValues)}</div>
      </div>
    );
  }).filter(Boolean);

  const CARD = { borderRadius: 10, padding: "8px 13px" };
  // ONE SOURCE. The ladder is composed server-side and stored on the row; this only locates it,
  // through the same three shapes every other field on this component is read through.
  const _fr = r.fullReport || r.full_report || {};
  const tiers = r.tierLadder || _fr.tierLadder || null;
  // The signed movement block, located through the same three shapes as every other field here.
  // Absent on every report that has not signed one, which is all of them but Santana's Dec-19 today.
  // ONE HOME PER VALUE. While the ladder still states the three medians in sentences, the picture
  // stays off — drawing them again would print one number twice, which is the defect this surface
  // has already had fixed twice. The hand-off is a ROW decision, not a release to be timed.
  const _mechBlockRaw = r.mechanicsCertified || _fr.mechanicsCertified || null;
  const mechBlock = (_mechBlockRaw && !ladderStatesValues(tiers, _mechBlockRaw)) ? _mechBlockRaw : null;
  return (
    <div style={{ width: "100%", maxWidth: 760, margin: "0 auto", background: "#fff", padding: "16px 22px", boxSizing: "border-box", fontFamily: "Inter,Arial,Helvetica,sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid " + BLUE, paddingBottom: 11 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {logo ? <img src={logo} alt="BBOT" style={{ height: 34 }} /> : <div style={{ font: "900 32px Inter,Arial", color: BLUE, letterSpacing: -1.5 }}>BB<span style={{ color: CORAL }}>{"●"}</span>T</div>}
          <div>
            <div style={{ font: "800 20px Inter,Arial", color: NAVY, lineHeight: 1.05 }}>{name}</div>
            <div style={{ font: "600 11px Inter,Arial", color: "#64748b", marginTop: 3 }}>{meta}</div>
          </div>
        </div>
      </div>
      <div style={{ font: "600 9.5px Inter,Arial", color: INK2, margin: "6px 0 8px", fontStyle: "italic" }}>every mark measured or observed, nothing inferred</div>

      {(r.radarPresentUnverified || (r.fullReport && r.fullReport.radarPresentUnverified) || (r.full_report && r.full_report.radarPresentUnverified)) ? (
        <div style={{ ...{ borderRadius: 10, padding: "7px 12px" }, background: "#fff7ed", border: "1px solid #fed7aa", marginBottom: 8, font: "600 9px Inter,Arial", color: "#9a3412" }}>
          ⚠ A radar overlay appears in this video but BBOT could not verify it as a Pocket Radar device — no velocity is reported (a reading is only shown when captured credibly).
        </div>
      ) : null}

      {/* SUMMARY DERIVED FROM GRADES, and from the SAME bracket the rest of the report uses. The
          stored summary drifted from the numbers beside it ("plus hit" over a Hit 40, 17U beside an
          18U header) because it was written on another path. Deriving it makes that impossible. */}
      {derivedSummary ? <>
        <Hdr title="SCOUT SUMMARY" />
        <div style={{ ...CARD, background: "#f4f8ff", border: "1px solid #dbe6fb", marginBottom: 7, font: "500 11px Inter,Arial", color: "#2b3a57", lineHeight: 1.55 }}>{derivedSummary}</div>
      </> : null}

      {/* SESSION RECORD — THE TIER LADDER, AND THE ONLY HOME IT HAS.
          Composed server-side by bbot_tier2 and read from the row, never rebuilt here: one source,
          so the player view and the PDF cannot drift into two versions of one fact. Every rep the
          footage contains appears at some tier — measured with receipts, observed without a proven
          subject, or absent with its cause. Tier 2 is device-sourced by construction; it can never
          describe motion from an unproven skeleton. */}
      {tiers ? <>
        <Hdr title="SESSION RECORD" sub="what was measured · what was observed · what was not" />
        <div style={{ ...CARD, background: "#fbfcff", border: "1px solid #e6edf8", marginBottom: 7 }}>
          {[["tier1", BLUE, "MEASURED"], ["tier2", "#6b7280", "OBSERVED"], ["tier3", CORAL, "NOT MEASURED"]]
            .map(function ([key, col, lbl]) {
              const lines = (tiers[key] || []).filter(Boolean);
              if (!lines.length) return null;
              return (
                <div key={key} style={{ marginBottom: 6 }}>
                  <div style={{ font: "700 8px Inter,Arial", color: col, letterSpacing: .6, marginBottom: 3 }}>{lbl}</div>
                  {lines.map(function (ln, i) {
                    return (
                      <div key={i} style={{ font: "400 10.5px Inter,Arial", color: "#33415c", lineHeight: 1.55, paddingLeft: 8, borderLeft: "2px solid " + col, marginBottom: 3 }}>{ln}</div>
                    );
                  })}
                </div>
              );
            })}
        </div>
      </> : null}

      {/* MOVEMENT MEASURED — the per-swing picture, drawn from the row's own series.
          It sits directly under SESSION RECORD because it is the same measurement the ladder's
          scope line introduces: the words say how far the measurement reaches, the picture says
          what it found. MechanicsChart reads the receipts itself and draws nothing that did not
          sign, so this call site cannot widen what appears -- there is no prop here that could. */}
      {mechBlock ? <>
        <Hdr title="MOVEMENT MEASURED" sub="one dot per swing · measured from video" />
        <MechanicsChart block={mechBlock} />
      </> : null}

      {bars.length ? <>
        <Hdr title="PERCENTILE vs PEERS" sub={"ranked · admin view · " + (bracket || "same-age peers")} />
        <div style={{ ...CARD, background: "#fbfcff", border: "1px solid #e6edf8", marginBottom: 7, paddingTop: 13 }}>
          {bars}
          <div style={{ font: "600 8.5px Inter,Arial", color: INK2, marginTop: 7, borderTop: "1px dashed #e6edf8", paddingTop: 5 }}>Only tools measured this session appear — uncaptured tools are omitted, never estimated.</div>
        </div>
      </> : null}

      {/* THE MECHANICS LAYER IS WITHHELD PLAYER-FACING until it returns proven. Every baseline report
          carried at least one physically impossible value -- a 2500 deg/s shoulder turn (about twice
          human maximum), a 109% knee lift (impossible unless both feet leave the ground), a 0.117 s
          swing tempo. A layer that fails plausibility on every report is not a layer with some bad
          values in it; it is unproven. Partial trust is how a false number reaches a family in the
          same font as a true one, so it returns WHOLE: 3D pose lift, a golden case verified against
          hand measurement at frames, Luis's signature, then bounds-gated rendering.

          Bounds are the floor, frames are the bar -- passing bounds never made a metric eligible.
          The skeleton render stays: it is visualisation, not asserted measurement. Admin keeps the
          values for diagnostics; the server withholds them from the player payload. */}
      {/* DECIDE BY STATE, NOT BY FLAG. The flag only got set when the eval itself withheld values, so
          four reports whose values were stripped by a different path showed no mechanics AND no
          line -- silently absent rather than honestly withheld, which is the outcome the ruling
          exists to prevent. What matters is what IS: a player we would measure, with nothing to
          show. Same law as position-derived labels -- derive from the state, not from a record of
          how the state was reached. */}
      {/* ONE TRUTH. The SESSION RECORD block above now states the withheld motion layer in the
          approved wording; this italic line said the same thing in different words, and a render of
          the five showed both on every report. Two sentences for one fact is the duplication the
          one-summary law exists to stop, so when the ladder is present it owns this statement. */}
      {!rows.length && kind !== "pitcher" && !tiers ? (
        <div style={{ ...CARD, background: "#fbfcff", border: "1px solid #e6edf8", marginBottom: 7,
                      font: "italic 9px Inter,Arial", color: INK2 }}>
          Motion analysis under verification.
        </div>
      ) : null}

      {rows.length ? <>
        <Hdr title="MEASURED MECHANICS" sub="result only — not ranked" />
        <div style={{ ...CARD, background: "#fffbfc", border: "1px solid #f3dbe0", marginBottom: 7 }}>
          <div style={{ font: "italic 8.5px Inter,Arial", color: INK2, marginBottom: 7 }}>Baseball marks his typical (median) value across {deliveries.length} {repWord}, on a low→high track.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1px 18px" }}>{rows}</div>
          {veloBars.length ? <>
            <div style={{ font: "800 8px Inter,Arial", color: BLUE, letterSpacing: 1, margin: "9px 0 4px", paddingTop: 7, borderTop: "1px solid #f3dbe0" }}>PITCH VELOCITY <span style={{ font: "600 7.5px Inter,Arial", color: INK2 }}>· radar</span></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1px 18px" }}>{veloBars}</div>
          </> : null}
          {glossItems.length ? (
            <div style={{ marginTop: 8, paddingTop: 6, borderTop: "1px solid #f3dbe0" }}>
              <div style={{ font: "700 7.5px Inter,Arial", color: INK2, letterSpacing: .5, marginBottom: 3 }}>WHAT THESE MEAN</div>
              {glossItems.map(function (g, i) {
                return <div key={i} style={{ font: "400 8px Inter,Arial", color: INK2, lineHeight: 1.5 }}><b style={{ color: "#64748b" }}>{g[0]}:</b>{" " + g[1]}</div>;
              })}
            </div>
          ) : null}
        </div>
      </> : null}

      {hitVeloBars.length ? <>
        <Hdr title="MEASURED VELOCITY" sub="device-verified · radar" />
        <div style={{ ...CARD, background: "#fbfcff", border: "1px solid #e6edf8", marginBottom: 7 }}>
          <div style={{ font: "italic 8.5px Inter,Arial", color: INK2, marginBottom: 7 }}>Device-read mph captured this session, on a low→high track. Exit velo off the bat and arm/throw velo, each read from the on-screen radar.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1px 18px" }}>{hitVeloBars}</div>
        </div>
      </> : null}

      {timedItems.length ? <>
        <Hdr title="MEASURED TIMES" sub="device / admin-verified · faster = better" />
        <div style={{ ...CARD, background: "#fbfcff", border: "1px solid #e6edf8", marginBottom: 7 }}>
          <div style={{ font: "italic 8.5px Inter,Arial", color: INK2, marginBottom: 7 }}>Home-to-1st over 90 ft and the 60-yard over 180 ft; catcher pop time is mitt-to-base. Faster is better; benchmarked to age.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1px 18px" }}>
            {timedItems.map(function (it, i) {
              return <TimeBar key={i} label={it[0]} value={it[1]} unit={it[2]} dec={2} grade={isAdmin ? it[3] : null} slow={it[4]} fast={it[5]} basis={it[6]} />;
            })}
          </div>
        </div>
      </> : null}

      {timedWithheld.length ? (
        <div style={{ ...CARD, background: "#fbfcff", border: "1px dashed #e6edf8", marginBottom: 7,
                      font: "italic 9px Inter,Arial", color: INK2 }}>
          {timedWithheld.join(" and ")} {timedWithheld.length > 1 ? "are" : "is"} on this session's
          record without a hand-timed entry behind {timedWithheld.length > 1 ? "them" : "it"}, so
          {timedWithheld.length > 1 ? " they are" : " it is"} not reported here. A time is published
          when a person has timed it.
        </div>
      ) : null}

      {/* data-drop-if-empty: the PDF strips video and iframe elements because a print cannot carry
          them, which left this section as a header over nothing whenever film was its only content.
          A heading with no content under it reads as something missing, not something absent. The
          print path removes any section marked this way once its content is empty. */}
      {(video || chart || pitchZone || sessionClips.length) ? <div data-drop-if-empty="1">
        <Hdr title="FILM & CHARTS" sub={"skeletal overlay · " + (kind === "pitcher" ? "strike zone" : "spray")} />
        <div style={{ display: "flex", gap: 12, marginBottom: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
          {video ? <div style={{ flex: "1 1 230px", minWidth: 0, maxWidth: 300 }}>{video}</div> : null}
          {chart ? <div style={{ flex: "0 1 185px", minWidth: 0, display: "flex", justifyContent: "center" }}>{chart}</div> : null}
          {pitchZone ? <div style={{ flex: "0 1 185px", minWidth: 0 }}>{pitchZone}</div> : null}
        </div>
        {/* THIS SESSION'S CLIPS, NOT THE PLAYER'S LIBRARY. A report and the footage it was made from
            lived as disconnected artifacts -- the clips sat on the profile and the report never
            pointed at them. These are the approved clips of THIS session only: a December report
            references December footage. The print path cannot carry players, so it keeps the
            reference line and the profile link instead (bbot-clip-players is stripped in print). */}
        {sessionClips.length ? (
          <div style={{ marginBottom: 8 }}>
            {/* NAMED BY WHAT THEY SHOW, not merely counted. "3 approved clips" tells a reader
                nothing about whether the film covers the at-bat, the throws or both; the kinds are
                already on each clip and were only being used on the tiles, which the print path
                strips. Distinct kinds, in the order they appear. */}
            <div style={{ font: "600 8.5px Inter,Arial", color: INK2, marginBottom: 5 }}>
              {sessionClips.length} approved clip{sessionClips.length > 1 ? "s" : ""} from this session
              {sc.sessionDate || r.sessionDate ? " (" + (sc.sessionDate || r.sessionDate) + ")" : ""}
              {(function () {
                var seen = [], i;
                for (i = 0; i < sessionClips.length; i++) {
                  var k = String((sessionClips[i] || {}).kind || "").trim();
                  if (k && seen.indexOf(k) < 0) seen.push(k);
                }
                return seen.length ? " — " + seen.join(" · ").toLowerCase() : "";
              })()}
              {" · full session footage on the player profile"}
            </div>
            <div className="bbot-clip-players"
                 style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {sessionClips.slice(0, 4).map(function (c, i) {
                return (
                  clipUrl(c.key) ? (
                  <div key={c.id || i} style={{ flex: "0 1 150px", minWidth: 0 }}>
                    <video src={clipUrl(c.key)} controls preload="metadata" playsInline
                           style={{ width: "100%", borderRadius: 6, background: "#000" }} />
                    <div style={{ font: "600 7.5px Inter,Arial", color: INK2, marginTop: 2 }}>
                      {String(c.kind || "clip").toUpperCase()}
                    </div>
                  </div>
                  ) : null
                );
              })}
            </div>
          </div>
        ) : null}
      </div> : null}

      {/* GRADES ARE ADMIN-ONLY. The 20-80 number is the one data point that is neither a direct
          measurement nor a pool-backed percentile -- a correlation layer that invites inflation and
          costs credibility. It stays COMPUTED and STORED (rubric, leaderboards, audit untouched);
          this is a render change and fully reversible. Players get the measured values and their
          position against the age reference, which is all that is defensible. */}
      {isAdmin && adminGradeLine ? (
        <div className="bbot-admin-only" style={{ ...CARD, background: "#fff7ed", border: "1px dashed #f6a96b", marginBottom: 7,
                      font: "500 10.5px Inter,Arial", color: "#7c4a12" }}>
          <div style={{ font: "800 8.5px Inter,Arial", letterSpacing: 1, marginBottom: 3 }}>
            ADMIN ONLY &mdash; NOT ON PLAYER REPORT
          </div>
          {adminGradeLine}
        </div>
      ) : null}
      {/* Wrapped so the PDF can strip it: the print path clones the live DOM, so an admin's
          download would otherwise carry admin-only content into the artifact a coach receives. */}
      {isAdmin && secs.length ? <div className="bbot-admin-only">
        <Hdr title="SCOUTING & MECHANICAL OBSERVATIONS" sub="grades &middot; percentiles &middot; sample_n &mdash; admin only, not on player report" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 16 }}>{secs}</div>
      </div> : null}

      <div style={{ marginTop: 14, borderTop: "1px solid #dbe6fb", paddingTop: 8, font: "500 8.5px Inter,Arial", color: INK2, lineHeight: 1.5 }}>
        Point-in-time — a snapshot, <b>not a projection</b>. (radar) = device overlay, (measured) = pose/CV or on-screen timer.
        <span style={{ float: "right", color: BLUE, fontWeight: 700 }}>Evaluator: BBOT</span>
      </div>
    </div>
  );
}
